import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { Search } from 'lucide-react'

function matches(item, groupLabel, query) {
  const searchable = [
    item.label,
    groupLabel,
    item.description,
    ...(item.keywords || [])
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()

  return searchable.includes(query.trim().toLowerCase())
}

export default function AdminCommandPalette({
  groups,
  open,
  onOpen,
  onClose
}) {
  const [query, setQuery] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const inputRef = useRef(null)
  const listRef = useRef(null)
  const navigate = useNavigate()

  const items = useMemo(
    () =>
      groups.flatMap(group =>
        group.items.map(item => ({ ...item, groupLabel: group.label }))
      ),
    [groups]
  )

  const filteredItems = useMemo(
    () =>
      query
        ? items.filter(item => matches(item, item.groupLabel, query))
        : items,
    [items, query]
  )

  const close = useCallback(() => {
    setQuery('')
    setSelectedIndex(0)
    onClose()
  }, [onClose])

  const selectItem = useCallback(
    item => {
      navigate(item.path)
      close()
    },
    [close, navigate]
  )

  useEffect(() => {
    const handleShortcut = event => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        if (open) close()
        else onOpen()
      }
    }

    document.addEventListener('keydown', handleShortcut)
    return () => document.removeEventListener('keydown', handleShortcut)
  }, [close, onOpen, open])

  useEffect(() => {
    if (!open) return undefined
    const timer = setTimeout(() => inputRef.current?.focus(), 0)
    return () => clearTimeout(timer)
  }, [open])

  useEffect(() => {
    setSelectedIndex(0)
  }, [query])

  useEffect(() => {
    const selected = listRef.current?.children[selectedIndex]
    selected?.scrollIntoView?.({ block: 'nearest' })
  }, [selectedIndex])

  if (!open) return null

  const handleKeyDown = event => {
    if (event.key === 'Escape') {
      close()
    } else if (event.key === 'ArrowDown') {
      event.preventDefault()
      setSelectedIndex(current =>
        Math.min(current + 1, filteredItems.length - 1)
      )
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setSelectedIndex(current => Math.max(current - 1, 0))
    } else if (event.key === 'Enter' && filteredItems[selectedIndex]) {
      event.preventDefault()
      selectItem(filteredItems[selectedIndex])
    }
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center px-4 pt-[12vh]"
      role="dialog"
      aria-modal="true"
      aria-label="Search Platform Console"
    >
      <button
        type="button"
        className="fixed inset-0 bg-black/60"
        onClick={close}
        aria-label="Close Platform Console search"
      />
      <div
        className="relative w-full max-w-xl overflow-hidden border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-900"
        onKeyDown={handleKeyDown}
      >
        <div className="flex items-center border-b border-gray-200 px-4 dark:border-gray-700">
          <Search
            className="h-5 w-5 flex-none text-gray-400"
            aria-hidden="true"
          />
          <input
            ref={inputRef}
            value={query}
            onChange={event => setQuery(event.target.value)}
            className="min-w-0 flex-1 bg-transparent px-3 py-4 text-sm text-gray-950 placeholder:text-gray-400 focus:outline-none dark:text-white"
            placeholder="Search Platform Console"
            aria-label="Search Platform Console destinations"
            role="combobox"
            aria-expanded="true"
            aria-controls="admin-command-results"
          />
          <kbd className="border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[11px] text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400">
            Esc
          </kbd>
        </div>

        <ul
          id="admin-command-results"
          ref={listRef}
          role="listbox"
          className="max-h-[60vh] overflow-y-auto py-2"
        >
          {filteredItems.length === 0 ? (
            <li className="px-4 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
              No matching destinations
            </li>
          ) : (
            filteredItems.map((item, index) => {
              const Icon = item.icon
              const selected = selectedIndex === index
              return (
                <li
                  key={item.id}
                  role="option"
                  aria-selected={selected}
                  onMouseEnter={() => setSelectedIndex(index)}
                  onClick={() => selectItem(item)}
                  className={`mx-2 flex cursor-pointer items-center gap-3 px-3 py-2.5 ${
                    selected
                      ? 'bg-gray-100 text-gray-950 dark:bg-gray-800 dark:text-white'
                      : 'text-gray-700 dark:text-gray-300'
                  }`}
                >
                  <Icon className="h-4 w-4 flex-none" aria-hidden="true" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">
                      {item.label}
                    </span>
                    <span className="block truncate text-xs text-gray-500 dark:text-gray-400">
                      {item.groupLabel}
                    </span>
                  </span>
                </li>
              )
            })
          )}
        </ul>
      </div>
    </div>,
    document.body
  )
}
